import { findBannerByCode, getCleanBannerCode } from 'utils/promo-banner-utils';
import { useEffect, useMemo, useRef, useState } from 'react';

import { captureMessage } from '@sentry/nextjs';
import ChipList from 'components/widgets/chip/chip-list';
import CommentContainer from 'components/blog/comments/comment-container';
import ContentBanner from 'components/blog/content-banner/content-banner';
import ContentFormBanner from 'components/blog/content-banner/content-form-banner';
import dynamic from 'next/dynamic';
import Image from 'core/image';
import LandingSection from 'components/landing/components/landing-section';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import RandomNoteList from '../random-note-list/random-note-list';
import RelatedNote from 'components/blog/blog-content/related-note';
import Spacer from 'core/spacer';
import styles from 'styles/BlogContent.module.scss';
import TableResumeHeader from 'components/landing/index/table-resume-header';
import { useCulture } from 'context/CultureContext';
import { usePageInfo } from 'context/PageInfoContext';
import { useScroll } from 'hooks';
import { useTranslations } from 'next-intl';

const ShareSocial = dynamic(() => import('components/widgets/share-social'));

function getRandomInt (min = 0, max = 99999) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min) + min);
}

const parseUl = (children) => {
    try {
        return children.map((child) => {
            if (child.name === 'li') {
                let titleDisplay = '';

                const extractText = (nodes) => {
                    return nodes.map(node => {
                        if (node.type === 'text') {
                            return node.data;
                        }
                        if (node.name === 'strong' || node.name === 'span') {
                            return `<${node.name}>${extractText(node.children)}</${node.name}>`;
                        }
                        if (node.children !== undefined) {
                            return extractText(node.children);
                        }
                        return '';
                    }).join('');
                };
                titleDisplay = extractText(child.children);

                return { title: titleDisplay, id: child.children[0].attribs ? child.children[0].attribs.href : null };
            }

            if (child.name === 'ul') {
                return { title: 'ul', id: 'ul', children: parseUl(child.children) };
            }
            return null;
        });
    } catch (error) {
        return null;
    }
};

const renderBannerFromParagraph = (children, bannerList, culture) => {
    try {
        // Check if this paragraph contains a banner placeholder
        if (
            children !== null &&
            children !== undefined &&
            children.length === 1 &&
            children[0].type === 'text'
        ) {
            const text = children[0].data;

            // Check for banner pattern
            const bannerPattern = /\[banner:([^\]]+)\]/;
            const bannerMatch = text.match(bannerPattern);

            if (bannerMatch !== null) {
                let code = bannerMatch[1];
                if (code.includes('<') || code.includes('>')) {
                    code = getCleanBannerCode(code);
                };

                const bannerConfig = findBannerByCode(bannerList, code);

                return bannerConfig !== undefined ?
                    <ContentFormBanner config={bannerConfig} culture={culture} /> :
                    <></>;
            }
        }
    } catch (err) {
        captureMessage(`blog-content: renderBannerFromParagraph failed: ${err?.message || String(err)}`);
        return <></>;
    }
};

export const convertToHtml = (rawContent, imageAlt, bannerList, culture) => {
    const HEIGHT_DEFAULT = '200';
    const WIDTH_DEFAULT = '400';

    const options = {
        replace: ({ name, children, attribs = {} }) => {
            if (name === 'ul') {
                const ulData = parseUl(children);
                if (ulData !== null) {
                    return <TableResumeHeader items={ulData} />;
                }
            } else if (name === 'img' && Object.keys(attribs).length !== 0) {
                // Get height value
                const styleAttr = attribs.style;
                let height = HEIGHT_DEFAULT;
                let width = WIDTH_DEFAULT;

                if (styleAttr !== undefined) {
                    const heightRegex = /height:(\d+)px/;
                    const heightMatch = styleAttr.match(heightRegex);
                    height = heightMatch ? heightMatch[1] : HEIGHT_DEFAULT;

                    // Get width value
                    const widthRegex = /width:(\d+)px/;
                    const widthMatch = styleAttr.match(widthRegex);
                    width = widthMatch ? widthMatch[1] : WIDTH_DEFAULT;
                }

                attribs.width = Number(width);
                attribs.height = Number(height);
                // Clean style attribute
                attribs.style = '';

                // Remove class attribute because it overrides the styles.image className
                if ('class' in attribs) {
                    delete attribs.class;
                };

                if (attribs.alt === '') {
                    attribs.alt = imageAlt.replace('[index]', getRandomInt());
                };

                return (
                    <Image {...attribs} className={styles.image} priority />
                );
            } else if (name === 'p') {
                const node = renderBannerFromParagraph(children, bannerList, culture);
                if (node !== null) {
                    return node;
                }
            }
        }
    };

    return parse(rawContent, options);
};

const BlogContent = () => {
    const [loadTotalPage, setLoadTotalPage] = useState(false);

    const { pageInfo } = usePageInfo();
    const { culture } = useCulture();
    const t = useTranslations('blog-content');
    const imageAlt = t('image-alt');

    const bannerListContent = pageInfo.page_context.content_promo_banner_list || [];
    const htmlContent = useMemo(
        () => convertToHtml(pageInfo.page_context.content, imageAlt, bannerListContent, culture),
        [pageInfo.page_context.content, imageAlt, culture]
    );

    const bannerList = pageInfo.page_context.lateral_promo_banner_list || [];
    const divRef = useRef(null);
    const [latteralColumnData, setLatteralColumnData] = useState([]);

    useEffect(() => {
        const height = divRef.current.clientHeight;

        // Adjust lateral column based on height
        let relatedNoteMax = 4;
        if (height < 800) {
            relatedNoteMax = 2;
        } else if (height >= 800 && height < 2000) {
            relatedNoteMax = 4;
        } else if (height >= 2000 && height < 3000) {
            relatedNoteMax = 6;
        } else if (height >= 3000 && height < 4000) {
            relatedNoteMax = 8;
        } else if (height >= 4000 && height < 6000) {
            relatedNoteMax = 12;
        } else if (height >= 6000) {
            relatedNoteMax = 15;
        };

        let displayRelatedNote = [];
        const fullRelatedNoteList = [];
        const shortRelatedNoteList = [];
        pageInfo.page_context.page_related_notes.map((note, index) => {
            if (note.image !== null && index < relatedNoteMax) {
                fullRelatedNoteList.push(<RelatedNote item={note} key={index} />);
            } else if (note.image === null && index < relatedNoteMax) {
                shortRelatedNoteList.push(<RelatedNote item={note} key={index} />);
            };
            return null;
        });

        displayRelatedNote = fullRelatedNoteList.concat(shortRelatedNoteList);

        const completeLatteralColumn = [];
        let bannerIndex = 0;
        if (bannerList !== null && bannerList.length > 0) {
            for (let index = 0; index < displayRelatedNote.length; index++) {
                if ((index + 1) % 4 === 0 || index === 0) {
                    completeLatteralColumn.push(<ContentBanner key={`banner-latteral-${index}`} config={bannerList[bannerIndex].config}
                        bannerType="lateral"
                        culture={culture}
                    />);
                    bannerIndex++;

                    // Reset banner index if needed
                    if (bannerIndex >= bannerList.length) {
                        bannerIndex = 0;
                    };
                }
                completeLatteralColumn.push(displayRelatedNote[index]);
            }
        }
        setLatteralColumnData(completeLatteralColumn.length > 0 ? completeLatteralColumn : displayRelatedNote);
    }, []);

    useScroll((scroll, unmountCallback) => {
        if (scroll > 0 && !loadTotalPage) {
            setLoadTotalPage(true);
            unmountCallback();
        };
    }, []);

    const tagItemList = useMemo(() => (pageInfo.page_context.tags || []).map((tag) => ({
        href: `/blog/?q=tag:${tag}`,
        text: `#${tag}`
    })), [pageInfo.page_context.tags]);

    return (
        <LandingSection disableBottomPadding disableTopPadding disableGradient>
            <div className={styles.container}>
                <div className={styles.content}>
                    {/* <div style={{ height: '100px', width: '100px', backgroundColor: 'red' }}></div> */}
                    <div className={styles.content_body} ref={divRef}>
                        {htmlContent}
                    </div>
                    <Spacer />
                    <ChipList itemList={tagItemList} />
                    {loadTotalPage && (
                        <div className={styles.share_container}>
                            <ShareSocial
                                title={pageInfo.page_context.title}
                                url={pageInfo.page_context.page_full_url}
                                minorPrice={pageInfo.page_context.page_dinamic_data.minor_price}
                            />
                        </div>
                    )}
                    <RandomNoteList
                        limitNoteInARow={6}
                        mobileLimitInARow={2}
                        randomNoteList={pageInfo.page_context.page_related_notes.slice().reverse()}
                    />
                    <CommentContainer />
                </div>
                <div className={styles.popular_notes}>
                    {latteralColumnData}
                </div>
            </div>
        </LandingSection>
    );
};

BlogContent.propTypes = {
    rawContent: PropTypes.string,
    relatedNoteList: PropTypes.array,
    bannerList: PropTypes.array
};

export default BlogContent;
